"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.AppBridgeCommandError = void 0;
exports.createAppBridgeClient = createAppBridgeClient;
const protocol_1 = require("./protocol");
function createAppBridgeClient(options) {
    const timeoutMs = options.timeoutMs ?? 5_000;
    const now = options.now ?? (() => new Date());
    const requestId = options.requestId ?? (() => globalThis.crypto.randomUUID());
    const pending = new Map();
    let context = null;
    let sessionToken = null;
    let destroyed = false;
    const onMessage = (event) => {
        if (destroyed ||
            event.origin !== options.hostOrigin ||
            event.source !== options.expectedHostWindow ||
            !(0, protocol_1.isSupportedAppBridgeProtocol)(event.data)) {
            return;
        }
        const parsed = protocol_1.appBridgeHostMessageSchema.safeParse(event.data);
        if (!parsed.success)
            return;
        const message = parsed.data;
        if (message.installationId !== options.installationId)
            return;
        if (message.type === "CREWFSM_ROUTE_CHANGED") {
            if (!context)
                return;
            context = { ...context, currentRoute: message.payload.currentRoute };
            options.onRouteChange?.(message.payload.currentRoute);
            return;
        }
        const request = pending.get(message.requestId);
        if (!request)
            return;
        clearTimeout(request.timeout);
        pending.delete(message.requestId);
        if (message.type === "CREWFSM_ERROR") {
            request.reject(new AppBridgeCommandError(message.payload.code, message.payload.message));
            return;
        }
        if (message.type === "CREWFSM_CONTEXT") {
            context = message.payload.context;
            sessionToken = message.payload.sessionToken;
        }
        request.resolve(message);
    };
    options.eventSource.addEventListener("message", onMessage);
    const send = (type, payload) => {
        if (destroyed)
            return Promise.reject(new Error("App Bridge is destroyed."));
        const currentRequestId = requestId();
        const message = {
            protocolVersion: protocol_1.APP_BRIDGE_PROTOCOL_VERSION,
            type,
            requestId: currentRequestId,
            installationId: options.installationId,
            timestamp: now().toISOString(),
            payload,
        };
        return new Promise((resolve, reject) => {
            const timeout = setTimeout(() => {
                pending.delete(currentRequestId);
                reject(new AppBridgeCommandError("TIMEOUT", "App Bridge request timed out."));
            }, timeoutMs);
            pending.set(currentRequestId, { resolve, reject, timeout });
            options.hostWindow.postMessage(message, options.hostOrigin);
        });
    };
    const acknowledge = async (type, payload) => {
        await send(type, payload);
    };
    return {
        async connect() {
            await send("CREWFSM_APP_READY", {
                clientVersion: options.clientVersion ?? "1.0.0",
                currentRoute: options.currentRoute,
            });
            if (!context)
                throw new Error("App Bridge context was not established.");
            return context;
        },
        destroy() {
            if (destroyed)
                return;
            destroyed = true;
            options.eventSource.removeEventListener("message", onMessage);
            for (const request of pending.values()) {
                clearTimeout(request.timeout);
                request.reject(new Error("App Bridge was destroyed."));
            }
            pending.clear();
            sessionToken = null;
            context = null;
        },
        getContext() {
            if (!context)
                throw new Error("App Bridge is not connected.");
            return context;
        },
        getSessionToken() {
            if (!sessionToken)
                throw new Error("App Bridge is not connected.");
            return sessionToken;
        },
        navigate: (path) => acknowledge("CREWFSM_NAVIGATE", { path }),
        openResource: (resourceType, resourceId) => acknowledge("CREWFSM_OPEN_RESOURCE", { resourceType, resourceId }),
        setTitle: (title) => acknowledge("CREWFSM_SET_TITLE", { title }),
        setLoading: (loading) => acknowledge("CREWFSM_SET_LOADING", { loading }),
        showToast: (message, tone = "INFO") => acknowledge("CREWFSM_SHOW_TOAST", { message, tone }),
        openModal: (modalId) => acknowledge("CREWFSM_OPEN_MODAL", { modalId }),
        closeModal: (modalId) => acknowledge("CREWFSM_CLOSE_MODAL", {
            ...(modalId === undefined ? {} : { modalId }),
        }),
        requestResize: (height) => acknowledge("CREWFSM_REQUEST_RESIZE", { height }),
        async refreshContext() {
            await send("CREWFSM_REFRESH_CONTEXT", {});
            if (!context)
                throw new Error("App Bridge context was not refreshed.");
            return context;
        },
    };
}
class AppBridgeCommandError extends Error {
    code;
    name = "AppBridgeCommandError";
    constructor(code, message) {
        super(message);
        this.code = code;
    }
}
exports.AppBridgeCommandError = AppBridgeCommandError;
//# sourceMappingURL=client.js.map